/**
 * useFeatureGate Hook (Story 2.2)
 *
 * Returns gating state for a specific feature, including whether access
 * is allowed, the limit, current usage, and upgrade plan recommendation.
 *
 * Used for upgrade modals and conditional UI rendering.
 */

import { useAuthStore } from "../stores/authStore";
import {
  PLAN_LIMITS,
  SAFETY_CAPS,
  getMinimumPlanForFeature,
  type Plan,
  type PlanFeature,
} from "@provenleap/types";

export interface UseFeatureGateResult {
  allowed: boolean;
  limit: number | null;
  current?: number;
  upgradePlan?: Plan;
  isUnlimited: boolean;
  /** False when currentCount was not provided for a numeric feature — `allowed` is not a real check. */
  checked: boolean;
}

/**
 * @param feature - The feature key to check access for
 * @param currentCount - Current usage count (required for numeric features)
 */
export function useFeatureGate(
  feature: PlanFeature,
  currentCount?: number
): UseFeatureGateResult {
  const plan: Plan = useAuthStore((s) => s.user?.plan) ?? "free";
  const limits = PLAN_LIMITS[plan];
  const limit = limits[feature];

  // Boolean features
  if (typeof limit === "boolean") {
    if (limit) {
      return { allowed: true, limit: null, isUnlimited: false, checked: true };
    }
    return {
      allowed: false,
      limit: null,
      upgradePlan: getMinimumPlanForFeature(feature, true),
      isUnlimited: false,
      checked: true,
    };
  }

  // String features — always "allowed" from a gating perspective
  if (typeof limit === "string") {
    return { allowed: true, limit: null, isUnlimited: false, checked: true };
  }

  // Numeric features without count — return limit info only, NOT a real access check
  if (currentCount === undefined) {
    return {
      allowed: true,
      limit,
      isUnlimited: limit === null,
      checked: false,
    };
  }

  // Null limit = unlimited (check safety cap)
  if (limit === null) {
    const safetyCap = SAFETY_CAPS[feature];
    if (safetyCap !== undefined && currentCount >= safetyCap) {
      return {
        allowed: false,
        limit: safetyCap,
        current: currentCount,
        isUnlimited: false,
        checked: true,
      };
    }
    return {
      allowed: true,
      limit: null,
      current: currentCount,
      isUnlimited: true,
      checked: true,
    };
  }

  // Count-based: currentCount < limit means allowed
  if (currentCount < limit) {
    return { allowed: true, limit, current: currentCount, isUnlimited: false, checked: true };
  }

  const nextCount = currentCount + 1;
  const minPlan = getMinimumPlanForFeature(feature, nextCount);
  return {
    allowed: false,
    limit,
    current: currentCount,
    // Don't suggest upgrading to the same plan (top-tier at limit)
    upgradePlan: minPlan !== plan ? minPlan : undefined,
    isUnlimited: false,
    checked: true,
  };
}
